/**
 * Admin SEO — Página completa estilo Yoast/RankMath
 * 
 * 5 Tabs: Geral, Páginas, Sitemap, robots.txt, Redirects
 * Features: SEO Score, Social Preview, Twitter Cards, Redirects CRUD
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { T } from '../styles/tokens';

// ── Estilos ──
const s = {
  page: { padding: '24px 32px', maxWidth: 1400, margin: '0 auto' } as any,
  title: { fontSize: 24, fontWeight: 700, color: T.text, marginBottom: 8 } as any,
  subtitle: { fontSize: 14, color: T.textMuted, marginBottom: 24 } as any,
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24, overflowX: 'auto' as any } as any,
  tab: (active: boolean) => ({
    padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
    color: active ? T.primary : T.textSecondary, background: 'none', border: 'none',
    borderBottom: `2px solid ${active ? T.primary : 'transparent'}`,
    whiteSpace: 'nowrap' as any, transition: 'all 0.15s',
  }),
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 16 } as any,
  input: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any, marginBottom: 12 } as any,
  textarea: { width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any, marginBottom: 12, minHeight: 80, resize: 'vertical' as any, fontFamily: 'inherit' } as any,
  select: { padding: '8px 12px', borderRadius: 8, fontSize: 14, background: T.bgInput, border: `1px solid ${T.border}`, color: T.text, outline: 'none', boxSizing: 'border-box' as any, marginBottom: 12, width: '100%' } as any,
  label: { fontSize: 12, fontWeight: 600, color: T.textMuted, display: 'block', marginBottom: 4 } as any,
  btn: (variant: 'primary' | 'ghost' | 'danger' | 'success') => ({
    padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s',
    ...(variant === 'primary' ? { background: T.primary, color: '#fff' } :
       variant === 'danger' ? { background: T.danger, color: '#fff' } :
       variant === 'success' ? { background: T.success, color: '#fff' } :
       { background: T.border, color: T.textSecondary }),
  }),
  row: { display: 'flex', gap: 12, marginBottom: 12 } as any,
  table: { width: '100%', borderCollapse: 'collapse' as any, fontSize: 13 } as any,
  th: { padding: '10px 12px', textAlign: 'left' as any, borderBottom: `1px solid ${T.border}`, color: T.textMuted, fontWeight: 600 } as any,
  td: { padding: '8px 12px', borderBottom: `1px solid ${T.border}`, color: T.text } as any,
  snippet: { background: '#fff', borderRadius: 8, padding: 16, maxWidth: 600, fontFamily: 'Arial, sans-serif' } as any,
  snippetTitle: { fontSize: 20, color: '#1a0dab', textDecoration: 'none', cursor: 'pointer', lineHeight: '1.3' } as any,
  snippetUrl: { fontSize: 14, color: '#006621', marginTop: 2 } as any,
  snippetDesc: { fontSize: 14, color: '#545454', marginTop: 4, lineHeight: '1.4' } as any,
  toggle: (on: boolean) => ({
    width: 40, height: 22, borderRadius: 11, cursor: 'pointer', border: 'none',
    background: on ? T.primary : T.border, position: 'relative' as any, transition: 'background 0.2s',
  }),
  toggleDot: (on: boolean) => ({
    width: 16, height: 16, borderRadius: '50%', background: '#fff',
    position: 'absolute' as any, top: 3, left: on ? 21 : 3, transition: 'left 0.2s',
  }),
};

// ── Tipos ──
interface SeoPage {
  id?: number;
  page_path: string;
  title: string;
  description: string;
  og_title: string;
  og_description: string;
  og_image: string;
  robots: string;
  canonical: string;
  structured_data_json: string;
  locale: string;
  twitter_title: string;
  twitter_description: string;
  twitter_image: string;
  twitter_card_type: string;
  include_in_sitemap: number;
  hreflang_alternates: string;
}

interface SeoRedirect {
  id?: number;
  from_path: string;
  to_url: string;
  status_code: number;
  is_active: number;
  hits: number;
}

const EMPTY_PAGE: SeoPage = {
  page_path: '/', title: '', description: '', og_title: '', og_description: '',
  og_image: '', robots: 'index, follow', canonical: '', structured_data_json: '', locale: 'pt-BR',
  twitter_title: '', twitter_description: '', twitter_image: '', twitter_card_type: 'summary_large_image',
  include_in_sitemap: 1, hreflang_alternates: '',
};

const EMPTY_REDIRECT: SeoRedirect = {
  from_path: '', to_url: '', status_code: 301, is_active: 1, hits: 0,
};

const DEFAULT_PAGES = [
  '/', '/login', '/dashboard', '/agents', '/billing', '/profile',
  '/admin', '/chat', '/solutions', '/pricing',
];

const ROBOTS_OPTIONS = ['index, follow', 'noindex, follow', 'noindex, nofollow', 'index, nofollow'];
const SEPARATOR_OPTIONS = [' | ', ' — ', ' - ', ' · '];
const TWITTER_CARD_TYPES = ['summary', 'summary_large_image'];
const LOCALE_OPTIONS = ['pt-BR', 'en-US', 'es-ES', 'fr-FR', 'de-DE'];

type TabId = 'geral' | 'paginas' | 'sitemap' | 'robots' | 'redirects';

// ═══ SEO Score Calculator ═══
function calcSeoScore(page: SeoPage) {
  const checks: { label: string; status: 'green' | 'yellow' | 'red'; info: string }[] = [];

  // Título
  const tLen = (page.title || '').length;
  if (tLen >= 50 && tLen <= 60) checks.push({ label: 'Título', status: 'green', info: `${tLen} chars (ideal: 50-60)` });
  else if (tLen > 0 && tLen < 70) checks.push({ label: 'Título', status: 'yellow', info: `${tLen} chars (ideal: 50-60)` });
  else checks.push({ label: 'Título', status: tLen === 0 ? 'red' : 'yellow', info: tLen === 0 ? 'Sem título' : `${tLen} chars` });

  // Descrição
  const dLen = (page.description || '').length;
  if (dLen >= 150 && dLen <= 160) checks.push({ label: 'Descrição', status: 'green', info: `${dLen} chars (ideal: 150-160)` });
  else if (dLen > 0 && dLen < 200) checks.push({ label: 'Descrição', status: 'yellow', info: `${dLen} chars (ideal: 150-160)` });
  else checks.push({ label: 'Descrição', status: dLen === 0 ? 'red' : 'yellow', info: dLen === 0 ? 'Sem descrição' : `${dLen} chars` });

  // OG Image
  checks.push({ label: 'OG Image', status: page.og_image ? 'green' : 'red', info: page.og_image ? '✓ Definida' : '⚠ Sem imagem OG' });

  // Canonical
  checks.push({ label: 'Canonical', status: page.canonical ? 'green' : 'yellow', info: page.canonical ? '✓ Definida' : 'ℹ Não definida (opcional)' });

  // Robots
  const isIndexed = (page.robots || '').includes('index') && !(page.robots || '').includes('noindex');
  checks.push({ label: 'Robots', status: isIndexed ? 'green' : 'yellow', info: page.robots || 'Não definido' });

  const score = Math.round((checks.filter(c => c.status === 'green').length / checks.length) * 100);
  return { checks, score };
}

// ═══ Componentes auxiliares ═══
function SeoScoreBar({ page }: { page: SeoPage }) {
  const { checks, score } = calcSeoScore(page);
  const color = score >= 80 ? T.success : score >= 50 ? T.warning : T.danger;

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
        <div style={{ fontSize: 28, fontWeight: 700, color }}>{score}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>SEO Score</div>
          <div style={{ height: 6, background: T.border, borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
      </div>
      {checks.map((c, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.status === 'green' ? T.success : c.status === 'yellow' ? T.warning : T.danger, flexShrink: 0 }} />
          <span style={{ color: T.text, fontWeight: 500, minWidth: 80 }}>{c.label}</span>
          <span style={{ color: T.textMuted }}>{c.info}</span>
        </div>
      ))}
    </div>
  );
}

function GooglePreview({ page }: { page: SeoPage }) {
  const displayTitle = page.title || page.og_title || 'Título da Página — WhiteLabelClaw';
  const displayDesc = page.description || page.og_description || 'Descrição da página...';
  const displayUrl = `${window.location.origin}${page.page_path}`;

  return (
    <div style={s.snippet}>
      <div style={s.snippetTitle}>{displayTitle}</div>
      <div style={s.snippetUrl}>{displayUrl}</div>
      <div style={s.snippetDesc}>{displayDesc.substring(0, 160)}</div>
    </div>
  );
}

function FacebookPreview({ page }: { page: SeoPage }) {
  const title = page.og_title || page.title || 'Título da Página';
  const desc = page.og_description || page.description || 'Descrição da página';
  const image = page.og_image;

  return (
    <div style={{ maxWidth: 500, border: '1px solid #dadde1', borderRadius: 3, overflow: 'hidden', background: '#f2f3f5', fontFamily: 'Helvetica, Arial, sans-serif' }}>
      {image && <div style={{ width: '100%', height: 260, background: `url(${image}) center/cover #e4e6eb` }} />}
      {!image && <div style={{ width: '100%', height: 80, background: '#e4e6eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#65676b', fontSize: 13 }}>Sem imagem OG</div>}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ fontSize: 12, color: '#65676b', textTransform: 'uppercase' as any }}>{window.location.host}</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#1d2129', marginTop: 2, lineHeight: '20px' }}>{title}</div>
        <div style={{ fontSize: 14, color: '#65676b', marginTop: 3, lineHeight: '18px' }}>{desc.substring(0, 120)}</div>
      </div>
    </div>
  );
}

function TwitterPreview({ page }: { page: SeoPage }) {
  const title = page.twitter_title || page.og_title || page.title || 'Título da Página';
  const desc = page.twitter_description || page.og_description || page.description || 'Descrição';
  const image = page.twitter_image || page.og_image;
  const isLarge = page.twitter_card_type === 'summary_large_image';

  if (isLarge) {
    return (
      <div style={{ maxWidth: 500, border: '1px solid #2f3336', borderRadius: 16, overflow: 'hidden', background: '#15202b', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
        {image && <div style={{ width: '100%', height: 250, background: `url(${image}) center/cover #2f3336` }} />}
        {!image && <div style={{ width: '100%', height: 80, background: '#2f3336', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8b98a5', fontSize: 13 }}>Sem imagem</div>}
        <div style={{ padding: '12px 14px' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#e7e9ea', lineHeight: '20px' }}>{title}</div>
          <div style={{ fontSize: 15, color: '#8b98a5', marginTop: 2, lineHeight: '20px' }}>{desc.substring(0, 100)}</div>
          <div style={{ fontSize: 13, color: '#8b98a5', marginTop: 4 }}>🔗 {window.location.host}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 500, border: '1px solid #2f3336', borderRadius: 16, overflow: 'hidden', background: '#15202b', display: 'flex', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      {image && <div style={{ width: 130, minHeight: 130, background: `url(${image}) center/cover #2f3336`, flexShrink: 0 }} />}
      <div style={{ padding: '12px 14px', flex: 1 }}>
        <div style={{ fontSize: 13, color: '#8b98a5' }}>{window.location.host}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#e7e9ea', marginTop: 2, lineHeight: '20px' }}>{title}</div>
        <div style={{ fontSize: 15, color: '#8b98a5', marginTop: 2, lineHeight: '20px' }}>{desc.substring(0, 80)}</div>
      </div>
    </div>
  );
}

// ═══ COMPONENTE PRINCIPAL ═══
export function AdminSEO() {
  const [tab, setTab] = useState<TabId>('geral');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // Tab Geral
  const [globals, setGlobals] = useState<Record<string, string>>({});

  // Tab Páginas
  const [pages, setPages] = useState<SeoPage[]>([]);
  const [editing, setEditing] = useState<SeoPage | null>(null);

  // Tab Robots
  const [robotsTxt, setRobotsTxt] = useState('');
  const [robotsPreview, setRobotsPreview] = useState('');

  // Tab Redirects
  const [redirects, setRedirects] = useState<SeoRedirect[]>([]);
  const [editingRedirect, setEditingRedirect] = useState<SeoRedirect | null>(null);

  // ── Carregar dados ──
  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    loadGlobals();
    loadPages();
    loadRobots();
    loadRedirects();
  }

  async function loadGlobals() {
    try {
      const res = await api.get('/admin/seo/globals') as any;
      setGlobals(res.data?.globals || res?.globals || {});
    } catch { setGlobals({}); }
  }

  async function loadPages() {
    try {
      const res = await api.get('/admin/seo/pages') as any;
      setPages(res.data?.pages || res?.pages || []);
    } catch { setPages([]); }
  }

  async function loadRobots() {
    try {
      const res = await api.get('/admin/seo/robots') as any;
      setRobotsTxt(res.data?.content || res?.content || '');
    } catch { setRobotsTxt(''); }
    try {
      const res = await fetch('/robots.txt');
      setRobotsPreview(await res.text());
    } catch { setRobotsPreview(''); }
  }

  async function loadRedirects() {
    try {
      const res = await api.get('/admin/seo/redirects') as any;
      setRedirects(res.data?.redirects || res?.redirects || []);
    } catch { setRedirects([]); }
  }

  function showMsg(text: string) { setMsg(text); setTimeout(() => setMsg(''), 3000); }

  // ── Ações ──
  async function saveGlobals() {
    setSaving(true);
    try { await api.put('/admin/seo/globals', globals); showMsg('✅ Configurações globais salvas!'); }
    catch (e: any) { showMsg(`❌ Erro: ${e?.message || 'Falha'}`); }
    setSaving(false);
  }

  async function savePage() {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put('/admin/seo/pages', editing);
      showMsg('✅ Página SEO salva!');
      await loadPages();
    } catch (e: any) { showMsg(`❌ Erro: ${e?.message || 'Falha'}`); }
    setSaving(false);
  }

  async function saveRobots() {
    setSaving(true);
    try { await api.put('/admin/seo/robots', { content: robotsTxt }); showMsg('✅ robots.txt salvo!'); }
    catch (e: any) { showMsg(`❌ Erro: ${e?.message || 'Falha'}`); }
    setSaving(false);
  }

  async function saveRedirect() {
    if (!editingRedirect) return;
    setSaving(true);
    try {
      await api.put('/admin/seo/redirects', editingRedirect);
      showMsg('✅ Redirect salvo!');
      await loadRedirects();
      setEditingRedirect(null);
    } catch (e: any) { showMsg(`❌ Erro: ${e?.message || 'Falha'}`); }
    setSaving(false);
  }

  async function deleteRedirect(id: number) {
    if (!confirm('Remover este redirect?')) return;
    try {
      await api.delete(`/admin/seo/redirects/${id}`);
      showMsg('✅ Redirect removido!');
      await loadRedirects();
    } catch (e: any) { showMsg(`❌ Erro: ${e?.message || 'Falha'}`); }
  }

  async function toggleSitemap(page: SeoPage) {
    try {
      await api.put('/admin/seo/pages', { ...page, include_in_sitemap: page.include_in_sitemap ? 0 : 1 });
      await loadPages();
    } catch { /* silencioso */ }
  }

  // ── Helpers ──
  function setGlobal(key: string, value: string) {
    setGlobals(prev => ({ ...prev, [key]: value }));
  }

  const TABS: { id: TabId; label: string; icon: string }[] = [
    { id: 'geral', label: 'Geral', icon: '⚙️' },
    { id: 'paginas', label: 'Páginas', icon: '📄' },
    { id: 'sitemap', label: 'Sitemap', icon: '🗺️' },
    { id: 'robots', label: 'robots.txt', icon: '🤖' },
    { id: 'redirects', label: 'Redirects', icon: '↪️' },
  ];

  return (
    <div style={s.page}>
      <h1 style={s.title}>🔍 SEO — Configurações Avançadas</h1>
      <p style={s.subtitle}>Gerencie SEO completo: metadados, Open Graph, Twitter Cards, sitemap, robots.txt e redirects</p>

      {msg && (
        <div style={{ padding: '8px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 500,
          background: msg.startsWith('✅') ? `${T.success}20` : `${T.danger}20`,
          color: msg.startsWith('✅') ? T.success : T.danger, border: `1px solid ${msg.startsWith('✅') ? T.success : T.danger}30` }}>
          {msg}
        </div>
      )}

      {/* Tab Bar */}
      <div style={s.tabBar}>
        {TABS.map(t => (
          <button key={t.id} style={s.tab(tab === t.id)} onClick={() => { setTab(t.id); setEditing(null); setEditingRedirect(null); }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: GERAL ═══ */}
      {tab === 'geral' && (
        <div>
          <div style={s.card}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>🌐 Configurações Globais de SEO</h3>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Site Title</label>
                <input style={s.input} value={globals.seo_site_title || ''} placeholder="WhiteLabelClaw"
                  onInput={(e: any) => setGlobal('seo_site_title', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Title Separator</label>
                <select style={s.select} value={globals.seo_separator || ' | '} onChange={(e: any) => setGlobal('seo_separator', e.target.value)}>
                  {SEPARATOR_OPTIONS.map(sep => <option key={sep} value={sep}>{`"${sep.trim()}"`}</option>)}
                </select>
              </div>
            </div>

            <label style={s.label}>Default Meta Description</label>
            <textarea style={s.textarea} value={globals.seo_default_description || ''} placeholder="Descrição padrão do site..."
              onInput={(e: any) => setGlobal('seo_default_description', e.target.value)} />

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Default OG Image URL</label>
                <input style={s.input} value={globals.seo_default_og_image || ''} placeholder="https://..."
                  onInput={(e: any) => setGlobal('seo_default_og_image', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Default Robots</label>
                <select style={s.select} value={globals.seo_default_robots || 'index, follow'} onChange={(e: any) => setGlobal('seo_default_robots', e.target.value)}>
                  {ROBOTS_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Twitter Site Handle</label>
                <input style={s.input} value={globals.seo_twitter_handle || ''} placeholder="@yourhandle"
                  onInput={(e: any) => setGlobal('seo_twitter_handle', e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Google Site Verification</label>
                <input style={s.input} value={globals.seo_google_verification || ''} placeholder="Código de verificação"
                  onInput={(e: any) => setGlobal('seo_google_verification', e.target.value)} />
              </div>
            </div>

            <label style={s.label}>Bing Site Verification</label>
            <input style={s.input} value={globals.seo_bing_verification || ''} placeholder="Código de verificação Bing"
              onInput={(e: any) => setGlobal('seo_bing_verification', e.target.value)} />

            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button style={s.btn('primary')} onClick={saveGlobals} disabled={saving}>
                {saving ? 'Salvando...' : '💾 Salvar Configurações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: PÁGINAS ═══ */}
      {tab === 'paginas' && !editing && (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <button style={s.btn('primary')} onClick={() => setEditing({ ...EMPTY_PAGE })}>+ Nova Página</button>
          </div>

          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Página</th>
                  <th style={s.th}>Título</th>
                  <th style={s.th}>Robots</th>
                  <th style={s.th}>Sitemap</th>
                  <th style={s.th}>Score</th>
                  <th style={s.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(p => {
                  const { score } = calcSeoScore(p as any);
                  const scoreColor = score >= 80 ? T.success : score >= 50 ? T.warning : T.danger;
                  return (
                    <tr key={p.page_path}>
                      <td style={s.td}><code>{p.page_path}</code></td>
                      <td style={s.td}>{p.title || <span style={{ color: T.textMuted }}>—</span>}</td>
                      <td style={s.td}><span style={{ fontSize: 11, color: T.textMuted }}>{p.robots}</span></td>
                      <td style={s.td}>{(p as any).include_in_sitemap ? '✅' : '❌'}</td>
                      <td style={s.td}><span style={{ fontWeight: 700, color: scoreColor }}>{score}</span></td>
                      <td style={s.td}>
                        <button style={s.btn('ghost')} onClick={() => setEditing({ ...EMPTY_PAGE, ...p } as any)}>✏️ Editar</button>
                      </td>
                    </tr>
                  );
                })}
                {DEFAULT_PAGES.filter(dp => !pages.find(p => p.page_path === dp)).map(dp => (
                  <tr key={dp}>
                    <td style={s.td}><code>{dp}</code></td>
                    <td style={{ ...s.td, color: T.textMuted }}>Não configurado</td>
                    <td style={s.td}>—</td>
                    <td style={s.td}>—</td>
                    <td style={s.td}>—</td>
                    <td style={s.td}>
                      <button style={s.btn('ghost')} onClick={() => setEditing({ ...EMPTY_PAGE, page_path: dp })}>➕ Configurar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Editor de Página */}
      {tab === 'paginas' && editing && (
        <div>
          <button style={s.btn('ghost')} onClick={() => setEditing(null)}>← Voltar à lista</button>

          {/* SEO Score */}
          <div style={{ marginTop: 16 }}>
            <SeoScoreBar page={editing} />
          </div>

          {/* Campos básicos */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>📝 SEO Básico — {editing.page_path}</h3>

            <label style={s.label}>Caminho da Página</label>
            <input style={s.input} value={editing.page_path}
              onInput={(e: any) => setEditing({ ...editing, page_path: e.target.value })} />

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Título (title tag) — {(editing.title || '').length} chars</label>
                <input style={s.input} value={editing.title} placeholder="WhiteLabelClaw — Agentes IA"
                  onInput={(e: any) => setEditing({ ...editing, title: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Robots</label>
                <select style={s.select} value={editing.robots || 'index, follow'}
                  onChange={(e: any) => setEditing({ ...editing, robots: e.target.value })}>
                  {ROBOTS_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>

            <label style={s.label}>Meta Description — {(editing.description || '').length} chars</label>
            <textarea style={s.textarea} value={editing.description} placeholder="Descrição da página..."
              onInput={(e: any) => setEditing({ ...editing, description: e.target.value })} />

            <label style={s.label}>Canonical URL</label>
            <input style={s.input} value={editing.canonical} placeholder={`${window.location.origin}/...`}
              onInput={(e: any) => setEditing({ ...editing, canonical: e.target.value })} />

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Incluir no Sitemap</label>
                <button style={s.toggle(!!editing.include_in_sitemap)} onClick={() => setEditing({ ...editing, include_in_sitemap: editing.include_in_sitemap ? 0 : 1 })}>
                  <div style={s.toggleDot(!!editing.include_in_sitemap)} />
                </button>
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Hreflang / Locale</label>
                <select style={s.select} value={editing.hreflang_alternates || ''} onChange={(e: any) => setEditing({ ...editing, hreflang_alternates: e.target.value })}>
                  <option value="">Nenhum</option>
                  {LOCALE_OPTIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Open Graph */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>📘 Open Graph (Facebook)</h3>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>OG Title</label>
                <input style={s.input} value={editing.og_title} placeholder="Título Open Graph"
                  onInput={(e: any) => setEditing({ ...editing, og_title: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>OG Image URL</label>
                <input style={s.input} value={editing.og_image} placeholder="https://..."
                  onInput={(e: any) => setEditing({ ...editing, og_image: e.target.value })} />
              </div>
            </div>

            <label style={s.label}>OG Description</label>
            <textarea style={s.textarea} value={editing.og_description} placeholder="Descrição Open Graph"
              onInput={(e: any) => setEditing({ ...editing, og_description: e.target.value })} />
          </div>

          {/* Twitter Card */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>🐦 Twitter Card</h3>

            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Twitter Title</label>
                <input style={s.input} value={editing.twitter_title} placeholder="Título para Twitter"
                  onInput={(e: any) => setEditing({ ...editing, twitter_title: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={s.label}>Card Type</label>
                <select style={s.select} value={editing.twitter_card_type || 'summary_large_image'}
                  onChange={(e: any) => setEditing({ ...editing, twitter_card_type: e.target.value })}>
                  {TWITTER_CARD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <label style={s.label}>Twitter Description</label>
            <textarea style={s.textarea} value={editing.twitter_description} placeholder="Descrição para Twitter"
              onInput={(e: any) => setEditing({ ...editing, twitter_description: e.target.value })} />

            <label style={s.label}>Twitter Image URL</label>
            <input style={s.input} value={editing.twitter_image} placeholder="https://..."
              onInput={(e: any) => setEditing({ ...editing, twitter_image: e.target.value })} />
          </div>

          {/* Structured Data */}
          <div style={{ ...s.card, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>📊 Dados Estruturados (JSON-LD)</h3>
            <textarea style={{ ...s.textarea, fontFamily: 'monospace', minHeight: 120 }}
              value={editing.structured_data_json}
              placeholder='{"@context": "https://schema.org", "@type": "WebPage", ...}'
              onInput={(e: any) => setEditing({ ...editing, structured_data_json: e.target.value })} />
          </div>

          {/* Salvar */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16, marginBottom: 16 }}>
            <button style={s.btn('primary')} onClick={savePage} disabled={saving}>
              {saving ? 'Salvando...' : '💾 Salvar Página'}
            </button>
          </div>

          {/* Previews */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div style={s.card}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 12 }}>👁️ Preview Google</h4>
              <GooglePreview page={editing} />
            </div>
            <div style={s.card}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 12 }}>📘 Preview Facebook</h4>
              <FacebookPreview page={editing} />
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={s.card}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 12 }}>🐦 Preview Twitter</h4>
              <TwitterPreview page={editing} />
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: SITEMAP ═══ */}
      {tab === 'sitemap' && (
        <div>
          <div style={{ ...s.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, margin: 0 }}>🗺️ Gerenciar Sitemap</h3>
              <a href="/sitemap.xml" target="_blank" style={{ fontSize: 13, color: T.primary, textDecoration: 'none' }}>
                🔗 Ver sitemap.xml →
              </a>
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', background: `${T.primary}15`, borderRadius: 8, border: `1px solid ${T.primary}30` }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Total de URLs</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{pages.filter(p => (p as any).include_in_sitemap).length}</div>
              </div>
              <div style={{ padding: '12px 16px', background: `${T.warning}15`, borderRadius: 8, border: `1px solid ${T.warning}30` }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Excluídas</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{pages.filter(p => !(p as any).include_in_sitemap).length}</div>
              </div>
              <div style={{ padding: '12px 16px', background: `${T.success}15`, borderRadius: 8, border: `1px solid ${T.success}30` }}>
                <div style={{ fontSize: 11, color: T.textMuted }}>Cache</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>1 hora</div>
              </div>
            </div>

            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Página</th>
                  <th style={s.th}>Título</th>
                  <th style={s.th}>Incluir no Sitemap</th>
                </tr>
              </thead>
              <tbody>
                {pages.map(p => (
                  <tr key={p.page_path}>
                    <td style={s.td}><code>{p.page_path}</code></td>
                    <td style={s.td}>{p.title || <span style={{ color: T.textMuted }}>—</span>}</td>
                    <td style={s.td}>
                      <button style={s.toggle(!!(p as any).include_in_sitemap)} onClick={() => toggleSitemap(p as any)}>
                        <div style={s.toggleDot(!!(p as any).include_in_sitemap)} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Preview do sitemap */}
          <div style={s.card}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 12 }}>Preview — URLs no Sitemap</h4>
            <div style={{ background: T.bgInput, borderRadius: 8, padding: 12, fontFamily: 'monospace', fontSize: 12, color: T.textMuted, maxHeight: 300, overflowY: 'auto' as any }}>
              <div style={{ color: T.primary }}>&lt;?xml version="1.0" encoding="UTF-8"?&gt;</div>
              <div style={{ color: T.primary }}>&lt;urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"&gt;</div>
              {pages.filter(p => (p as any).include_in_sitemap).map(p => (
                <div key={p.page_path} style={{ paddingLeft: 16 }}>
                  <div>&lt;url&gt;</div>
                  <div style={{ paddingLeft: 16 }}>&lt;loc&gt;{`${window.location.origin}${p.page_path}`}&lt;/loc&gt;</div>
                  <div style={{ paddingLeft: 16 }}>&lt;changefreq&gt;weekly&lt;/changefreq&gt;</div>
                  <div>&lt;/url&gt;</div>
                </div>
              ))}
              <div style={{ color: T.primary }}>&lt;/urlset&gt;</div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ TAB: ROBOTS.TXT ═══ */}
      {tab === 'robots' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={s.card}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>✏️ Editor robots.txt</h3>
            <textarea
              style={{ ...s.textarea, fontFamily: 'monospace', minHeight: 300, fontSize: 13 }}
              value={robotsTxt}
              placeholder={'User-agent: *\nAllow: /\n\nSitemap: {`${window.location.origin}/sitemap.xml`}'}
              onInput={(e: any) => setRobotsTxt(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={s.btn('primary')} onClick={saveRobots} disabled={saving}>
                {saving ? 'Salvando...' : '💾 Salvar'}
              </button>
              <button style={s.btn('ghost')} onClick={async () => {
                try { const r = await fetch('/robots.txt'); setRobotsTxt(await r.text()); }
                catch { showMsg('❌ Erro ao carregar robots.txt'); }
              }}>📥 Carregar Atual</button>
            </div>
          </div>

          <div style={s.card}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>👁️ Preview (atual em produção)</h3>
            <pre style={{ fontFamily: 'monospace', fontSize: 13, color: T.textMuted, background: T.bgInput, borderRadius: 8, padding: 12, minHeight: 300, overflowX: 'auto' as any, whiteSpace: 'pre-wrap' as any, margin: 0 }}>
              {robotsPreview || 'Nenhum robots.txt encontrado'}
            </pre>
            <a href="/robots.txt" target="_blank" style={{ fontSize: 13, color: T.primary, textDecoration: 'none', display: 'inline-block', marginTop: 8 }}>
              🔗 Abrir /robots.txt →
            </a>
          </div>
        </div>
      )}

      {/* ═══ TAB: REDIRECTS ═══ */}
      {tab === 'redirects' && (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <button style={s.btn('primary')} onClick={() => setEditingRedirect({ ...EMPTY_REDIRECT })}>+ Novo Redirect</button>
          </div>

          {/* Formulário de redirect */}
          {editingRedirect && (
            <div style={{ ...s.card, marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 16 }}>
                {editingRedirect.id ? '✏️ Editar Redirect' : '➕ Novo Redirect'}
              </h3>

              <div style={s.row}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>De (path)</label>
                  <input style={s.input} value={editingRedirect.from_path} placeholder="/antigo"
                    onInput={(e: any) => setEditingRedirect({ ...editingRedirect, from_path: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Para (URL)</label>
                  <input style={s.input} value={editingRedirect.to_url} placeholder={`${window.location.origin}/novo`}
                    onInput={(e: any) => setEditingRedirect({ ...editingRedirect, to_url: e.target.value })} />
                </div>
              </div>

              <div style={s.row}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Status Code</label>
                  <select style={s.select} value={editingRedirect.status_code}
                    onChange={(e: any) => setEditingRedirect({ ...editingRedirect, status_code: parseInt(e.target.value) })}>
                    <option value={301}>301 — Permanente</option>
                    <option value={302}>302 — Temporário</option>
                  </select>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: T.text }}>
                    <button style={s.toggle(!!editingRedirect.is_active)}
                      onClick={() => setEditingRedirect({ ...editingRedirect, is_active: editingRedirect.is_active ? 0 : 1 })}>
                      <div style={s.toggleDot(!!editingRedirect.is_active)} />
                    </button>
                    Ativo
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn('primary')} onClick={saveRedirect} disabled={saving}>
                  {saving ? 'Salvando...' : '💾 Salvar'}
                </button>
                <button style={s.btn('ghost')} onClick={() => setEditingRedirect(null)}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Tabela de redirects */}
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>De</th>
                  <th style={s.th}>Para</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Ativo</th>
                  <th style={s.th}>Hits</th>
                  <th style={s.th}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {redirects.length === 0 && (
                  <tr><td style={{ ...s.td, textAlign: 'center', color: T.textMuted }} colSpan={6}>Nenhum redirect configurado</td></tr>
                )}
                {redirects.map(r => (
                  <tr key={r.id}>
                    <td style={s.td}><code>{r.from_path}</code></td>
                    <td style={s.td}><span style={{ fontSize: 12, color: T.textMuted }}>{r.to_url}</span></td>
                    <td style={s.td}><span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: r.status_code === 301 ? `${T.success}20` : `${T.warning}20`, color: r.status_code === 301 ? T.success : T.warning }}>{r.status_code}</span></td>
                    <td style={s.td}>{r.is_active ? '✅' : '❌'}</td>
                    <td style={s.td}><span style={{ color: T.textMuted }}>{r.hits || 0}</span></td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button style={s.btn('ghost')} onClick={() => setEditingRedirect({ ...r })}>✏️</button>
                        <button style={s.btn('danger')} onClick={() => deleteRedirect(r.id!)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSEO;
